/**
 * LeagueHome (Checkpoint 2 - full version)
 * - Top: current week banner + (commissioner) Advance Week button
 * - Left: compact standings + power rank card
 * - Center: activity feed (placeholder until activity API arrives in Checkpoint 4)
 * - Right: top performers + upcoming matchups
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Calendar, Activity, Users, ChevronRight, Trophy, Crown } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useSeason } from "../contexts/SeasonContext";
import { playLine } from "../audio/auctioneerAudio";
import SeasonShell from "../components/season/SeasonShell";
import AdamAvatarBadge from "../components/AdamAvatarBadge";
import ManagerName from "../components/ManagerName";
import ActivitySpeakerButton from "../components/activity/ActivitySpeakerButton";
import ListenWithAdamHint from "../components/activity/ListenWithAdamHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeagueHome() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { actingAsTeamId } = useSeason();
  const navigate = useNavigate();

  const [season, setSeason] = useState(null);
  const [standings, setStandings] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [advanceBusy, setAdvanceBusy] = useState(false);
  const [advanceMsg, setAdvanceMsg] = useState(null);
  const [leagueMeta, setLeagueMeta] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadError, setLoadError] = useState(null);
  // [Phase 2c.6-A.1.11 RL-12 / RL-18 / RL-22] SEASON_WELCOME banner state.
  const [welcomeText, setWelcomeText] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [welcomeError, setWelcomeError] = useState(false);

  // [HF4.5] Round-start cinematic removed from dashboard. Founder spec:
  // round-start modal lives on the standalone draft board only. TD-47 pins
  // the future move to the snake dashboard's center activity panel post the
  // dashboard-overhaul checkpoint.

  // Lazy GET /welcome on mount post-bridge. Idempotent - backend returns
  // existing row or fires LLM. Auto-TTS plays ONCE per (user, league) via
  // localStorage flag, respects global mute (mute = skip flag write so
  // unmute on next visit replays).
  useEffect(() => {
    if (!leagueId || !user?.id) return;
    let cancelled = false;
    setWelcomeLoading(true);
    axios
      .get(`${API}/season/${leagueId}/welcome`, { headers: authHeaders() })
      .then((r) => {
        if (cancelled) return;
        const text = r?.data?.voice_text;
        if (!text) throw new Error("empty voice_text");
        setWelcomeText(text);
        // Auto-TTS gating
        const flagKey = `pgf_welcome_played:${leagueId}:${user.id}`;
        let alreadyPlayed = false;
        try { alreadyPlayed = localStorage.getItem(flagKey) === "1"; } catch (_e) {}
        const muted = (() => {
          try { return localStorage.getItem("pgf_audio_muted") === "1"; } catch (_e) { return false; }
        })();
        if (!alreadyPlayed && !muted) {
          playLine(text).finally(() => {
            try { localStorage.setItem(flagKey, "1"); } catch (_e) {}
          });
        }
      })
      .catch((e) => {
        // RL-18: graceful unmount + console.warn, no retries.
        // 404 = pre-bridge state; surface stays hidden.
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[LeagueHome] welcome fetch failed:", e?.message || e);
        setWelcomeError(true);
      })
      .finally(() => { if (!cancelled) setWelcomeLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId, user?.id]);

  useEffect(() => {
    if (!leagueId) return;
    axios.get(`${API}/activity/${leagueId}?limit=20`, { headers: authHeaders() })
      .then((r) => setActivity(r.data || []))
      .catch(() => setActivity([]));
  }, [leagueId, advanceMsg]);

  useEffect(() => {
    if (!leagueId) return;
    setLoadError(null);
    // Independent fetches - one failing does NOT block the others.
    // `season` is the only hard requirement to render the page.
    axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() })
      .then((r) => setSeason(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load season"));
    axios.get(`${API}/standings/${leagueId}`, { headers: authHeaders() })
      .then((r) => setStandings(r.data?.standings || []))
      .catch(() => setStandings([]));
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => setLeagueMeta(r.data))
      .catch(() => setLeagueMeta(null));
  }, [leagueId]);

  const isCommissioner = user && leagueMeta && leagueMeta.commissioner_id === user.id;

  const handleAdvanceWeek = async () => {
    if (!window.confirm(`Advance from Week ${season.current_week}? Lineups will be locked, scores generated, matchups finalized.`)) return;
    setAdvanceBusy(true); setAdvanceMsg(null);
    try {
      const r = await axios.post(`${API}/season/${leagueId}/advance-week`, {}, { headers: authHeaders() });
      setAdvanceMsg(`Week ${r.data.from_week} finalized → now in Week ${r.data.to_week}. ${r.data.matchups_finalized} matchups finalized.`);
      // Reload
      const [s, st] = await Promise.all([
        axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() }),
        axios.get(`${API}/standings/${leagueId}`, { headers: authHeaders() }),
      ]);
      setSeason(s.data); setStandings(st.data?.standings || []);
    } catch (e) {
      setAdvanceMsg(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally { setAdvanceBusy(false); }
  };

  useEffect(() => {
    if (!season) return;
    // Pull current week's matchups (uses team schedule for week 1 - multi team)
    // For Phase 1 we approximate by hitting team schedule for the first team; full /matchups router lands Checkpoint 3
    if (season.teams?.[0]?.id) {
      // Exploratory teaser only - non-essential. Per /docs/PGF_PHASE_1_5_SPEC_v1_1.md §1.1a:
      // optional reads degrade silently with a meaningful no-op handler.
      axios.get(`${API}/teams/${season.teams[0].id}/schedule`, { headers: authHeaders() })
        .then((r) => {
          const week = season.current_week;
          // synthesize all matchups from any team's schedule view at current_week + opponent lookup
          // simpler: fetch each team's current matchup
          // (deferred - Checkpoint 3 ships /matchups/league/:id/week/:week)
        })
        .catch((_e) => { /* optional teaser, non-essential */ });
    }
  }, [season]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  if (!season) {
    return (
      <SeasonShell>
        <div className="text-center py-20" data-testid="lh-loading">
          {loadError ? (
            <>
              <p className="font-display font-black uppercase text-xl text-[#FF4444] mb-2">League not active</p>
              <p className={`font-body text-sm ${muted} mb-1`}>{loadError}</p>
              <p className={`font-body text-xs ${muted}`}>
                This league hasn't been bridged to a season yet (status is likely SETUP or DRAFTING). Run the draft + bridge from the Simulator first.
              </p>
              <button
                onClick={() => navigate("/simulator")}
                className="mt-4 px-4 py-2 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase text-sm"
                data-testid="lh-go-to-simulator"
              >
                Open Simulator →
              </button>
            </>
          ) : (
            <p className="font-display font-black uppercase text-2xl text-[#FF6B00] mb-2 animate-pulse">Loading league…</p>
          )}
        </div>
      </SeasonShell>
    );
  }

  // Acting team / my team
  const myTeam = season.teams.find((t) => t.id === actingAsTeamId)
              || season.teams.find((t) => t.user_id === user?.id)
              || season.teams[0];

  // Power Rank: PF + recent form (no recent form yet → just PF rank)
  // Power Rank: spec §"4 Enhancements" - PF + recent form weighted, last 3 wks heavier.
  // Backend now emits power_score per team (0.6*PF + 0.4*recent_avg*GP). Sort desc.
  const powerRank = [...standings].sort((a, b) => (b.power_score || b.points_for) - (a.power_score || a.points_for));

  // Top of standings (top 6 - playoff line)
  const top6 = standings.slice(0, 6);

  // Phase 1.5 - status-aware view
  const leagueStatus = leagueMeta?.status || "SEASON_ACTIVE";
  const isPlayoffs = leagueStatus === "PLAYOFFS";
  const isSeasonComplete = leagueStatus === "SEASON_COMPLETE";

  return (
    <SeasonShell>
      {/* [Phase 2c.6-A.1.11 RL-12 / RL-18 / RL-22] SEASON_WELCOME banner.
          Shimmer → text + auto-TTS. One-shot per (user, league). */}
      {!welcomeError && (welcomeLoading || welcomeText) && (
        <div
          className={`rounded-2xl ${cardBg} border p-5 mb-6 relative overflow-hidden`}
          style={{
            borderColor: "rgba(255,107,0,0.5)",
            boxShadow: "0 0 0 1px rgba(255,107,0,0.15), 0 6px 24px rgba(0,0,0,0.25)",
          }}
          data-testid="lh-season-welcome"
        >
          {/* [Phase 2c.6-A.2.1 D-6 / RL-34] Unified Adam badge replaces the
              previous inline bar + label. Same orange tone, single source. */}
          <div className="mb-2">
            <AdamAvatarBadge label="ADAM · SEASON WELCOME" size="sm" />
          </div>
          {welcomeText ? (
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDark ? "text-[#F5F5F5]" : "text-[#1A1A1A]"} whitespace-pre-line`}
              data-testid="lh-welcome-text"
            >
              {welcomeText}
            </p>
          ) : (
            <div className="space-y-2" data-testid="lh-welcome-shimmer">
              <div className="h-3.5 bg-white/10 rounded animate-pulse w-4/5" />
              <div className="h-3.5 bg-white/10 rounded animate-pulse w-full" />
              <div className="h-3.5 bg-white/10 rounded animate-pulse w-3/4" />
              <p className={`pt-2 text-xs italic ${isDark ? "text-white/40" : "text-black/40"}`}>
                Adam's looking at the rosters...
              </p>
            </div>
          )}
        </div>
      )}

      {/* Phase 1.5 - Season Complete hero (replaces week banner) */}
      {isSeasonComplete && (
        <div
          className={`rounded-2xl ${cardBg} border ${cardBorder} p-6 mb-6 relative overflow-hidden`}
          style={{ background: isDark ? "linear-gradient(135deg, #1C1C1C 0%, #2A1F0A 100%)" : "linear-gradient(135deg, #FFFFFF 0%, #FFF8E7 100%)" }}
          data-testid="lh-season-complete"
        >
          <div className="flex items-start gap-4">
            <Crown size={48} className="text-[#FFD700] flex-shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF6B00] font-display font-bold mb-1">
                Season {season.season_year} · Complete
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-3">
                Champion Crowned
              </h1>
              <Link
                to={`/league/${leagueId}/recap`}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FFD700] hover:bg-[#FFE066] text-[#111111] font-display font-black uppercase tracking-wide text-sm transition-all shadow-lg shadow-[#FFD700]/30"
                data-testid="lh-view-recap"
              >
                View Season Recap →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1.5 - Playoffs Live banner */}
      {isPlayoffs && (
        <div
          className={`rounded-2xl ${cardBg} border-2 border-[#FF6B00] p-5 mb-6`}
          data-testid="lh-playoffs-banner"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Trophy size={32} className="text-[#FF6B00]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-0.5">
                  PGF Playoffs · Week {season.current_week}
                </div>
                <h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                  Playoffs Live
                </h1>
              </div>
            </div>
            <Link
              to={`/league/${leagueId}/playoffs`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase tracking-wide text-sm transition-all shadow-lg shadow-[#FF6B00]/30"
              data-testid="lh-view-bracket"
            >
              <Trophy size={14} /> View Bracket →
            </Link>
          </div>
        </div>
      )}

      {/* Current week banner (hidden when SEASON_COMPLETE - replaced by hero above) */}
      {!isSeasonComplete && (
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid="lh-week-banner">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">
            Season {season.season_year}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
            Week {season.current_week} of {season.regular_season_weeks}
          </h1>
          <p className={`mt-1 text-sm ${muted}`}>
            {myTeam ? <>You're acting as <span className="text-[#FF6B00] font-bold">{myTeam.team_name}</span></> : "Welcome"}
          </p>
        </div>
        {myTeam && (
          <button
            onClick={() => navigate(`/league/${leagueId}/team/${myTeam.id}/lineup/${season.current_week}`)}
            className="px-5 py-3 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase tracking-wide text-sm transition-all shadow-lg shadow-[#FF6B00]/20"
            data-testid="lh-set-lineup"
          >
            Set Lineup →
          </button>
        )}
        {isCommissioner && !isSeasonComplete && (
          <button
            onClick={handleAdvanceWeek}
            disabled={advanceBusy}
            className={`px-5 py-3 rounded-xl ${
              isPlayoffs
                ? "bg-[#FF6B00]/15 border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/25"
                : "bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] hover:bg-[#00C853]/25"
            } font-display font-black uppercase tracking-wide text-sm transition-all disabled:opacity-50`}
            data-testid="lh-advance-week"
          >
            {advanceBusy ? "Advancing…" : `Advance Week →`}
          </button>
        )}
      </div>
      )}
      {advanceMsg && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-[#00C853]/10 border border-[#00C853]/40 text-[#00C853] text-sm" data-testid="lh-advance-msg">
          {advanceMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT: Standings + Power Rank */}
        <div className="space-y-5">
          {/* Standings compact */}
          <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="lh-standings">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2">
                <Users size={14} className="text-[#FF6B00]" /> Standings
              </h2>
              <Link to={`/league/${leagueId}/standings`} className={`text-[10px] uppercase tracking-wider ${muted} hover:${fg}`}>
                Full <ChevronRight size={10} className="inline" />
              </Link>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className={`text-[10px] uppercase tracking-wider ${muted} text-left`}>
                  <th className="pb-2">#</th>
                  <th className="pb-2">Team</th>
                  <th className="pb-2 text-right">W-L-T</th>
                  <th className="pb-2 text-right">PF</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((r, idx) => {
                  const isPlayoff = idx < 6;
                  return (
                    <tr key={r.team_id} className={`${idx === 5 ? "border-b border-dashed border-[#FF6B00]/30" : ""} ${r.team_id === myTeam?.id ? "bg-[#FF6B00]/5" : ""}`}>
                      <td className={`py-1.5 ${isPlayoff ? "text-[#00C853] font-bold" : ""}`}>{r.rank}</td>
                      <td className="py-1.5 truncate max-w-[100px]" title={r.team_name}>
                        <ManagerName name={r.team_name} isBot={r.is_bot} iconSize={10} testId={`lh-stand-${r.team_id}`} />
                      </td>
                      <td className="py-1.5 text-right">{r.wins}-{r.losses}-{r.ties}</td>
                      <td className="py-1.5 text-right">{Math.round(r.points_for)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className={`text-[10px] mt-2 ${muted}`}>Dashed line = playoff cut (top 6).</p>
          </div>

          {/* Power Rank card */}
          <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="lh-power-rank">
            <h2 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-[#FF6B00]" /> Power Rank
            </h2>
            <div className="space-y-2">
              {powerRank.slice(0, 5).map((r, i) => (
                <div key={r.team_id} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2">
                    <span className="font-display font-black text-[#FF6B00] w-4">{i + 1}</span>
                    <span className="truncate max-w-[140px]">
                      <ManagerName name={r.team_name} isBot={r.is_bot} iconSize={10} testId={`lh-power-${r.team_id}`} />
                    </span>
                  </span>
                  <span className={`${muted}`}>{Math.round(r.points_for)} PF</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER: Activity feed (real, hybrid template+LLM voice) */}
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="lh-activity">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
            <Activity size={14} className="text-[#FF6B00]" /> Activity
          </h2>
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
            {activity.length === 0 ? (
              <ActivityItem
                isDark={isDark}
                text="Draft is in the books. Welcome to season 2026 - let the lineup arguments begin."
                voice="Adam"
                source="template"
              />
            ) : (
              activity.map((a, idx) => (
                <ActivityItem
                  key={a.id}
                  activityId={a.id}
                  isDark={isDark}
                  text={a.voice_text}
                  voice="Adam"
                  source={a.voice_source}
                  type={a.type}
                  testid={`lh-activity-${a.type}`}
                  showListenHint={idx === 0 && a.voice_source !== "system"}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Upcoming matchups */}
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="lh-matchups">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-[#FF6B00]" /> Week {season.current_week} Matchups
          </h2>
          <div className="space-y-2">
            <p className={`text-[10px] italic ${muted}`}>
              Matchup Center (head-to-head + projections + live ticker) lands in Checkpoint 3.
            </p>
            <MatchupTeaser leagueId={leagueId} week={season.current_week} teams={season.teams} myTeamId={myTeam?.id} isDark={isDark} />
          </div>
        </div>
      </div>

    </SeasonShell>
  );
}

function ActivityItem({ activityId, text, voice, isDark, source, type, testid, showListenHint = false }) {
  const muted = isDark ? "text-[#888]" : "text-[#666]";
  const isLLM = source === "llm";
  const speaker = (
    <ActivitySpeakerButton
      activityId={activityId}
      voiceText={text}
      voiceSource={source}
      testid={activityId ? `activity-speaker-${activityId}` : undefined}
    />
  );
  return (
    <div className="text-xs leading-relaxed" data-testid={testid}>
      <div className={`text-[9px] uppercase tracking-widest ${muted} mb-0.5 flex items-center gap-1.5`}>
        {voice && <span className="text-[#FF6B00] font-bold">{voice}</span>}
        {isLLM && <span className="px-1 py-px rounded bg-[#FF6B00]/15 text-[#FF6B00] text-[8px] tracking-widest">AI</span>}
        {type && <span className={`${muted}`}>· {type.replace(/_/g, " ").toLowerCase()}</span>}
      </div>
      <div className="flex items-start gap-2">
        <p className="flex-1">{text}</p>
        {showListenHint ? <ListenWithAdamHint>{speaker}</ListenWithAdamHint> : speaker}
      </div>
    </div>
  );
}

/** Pull the acting team's current week matchup as a teaser. */
function MatchupTeaser({ leagueId, week, teams, myTeamId, isDark }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (!myTeamId) return;
    // Optional teaser - degrades to null state if fetch fails.
    axios.get(`${API}/teams/${myTeamId}/schedule`, { headers: authHeaders() })
      .then((r) => {
        const cur = (r.data || []).find((x) => x.week === week);
        setM(cur);
      })
      .catch((_e) => setM(null));
  }, [myTeamId, week]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cardBg = isDark ? "bg-[#252525]" : "bg-[#F5F5F5]";

  if (!m) return null;
  const me = teams.find((t) => t.id === myTeamId)?.team_name || "You";
  return (
    <div className={`rounded-lg ${cardBg} p-3 mt-1`} data-testid="lh-matchup-teaser">
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#FF6B00] mb-1.5">
        {m.is_home ? "HOME" : "AWAY"} · Week {m.week}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-display font-bold uppercase truncate max-w-[80px]">{me}</span>
        <span className={`text-[10px] ${muted}`}>{m.my_projected ? `${m.my_projected.toFixed(1)} proj` : ""}</span>
      </div>
      <div className="text-[10px] text-center my-1">vs</div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-display font-bold uppercase truncate max-w-[80px]">
          <ManagerName name={m.opponent_team_name} isBot={m.opponent_is_bot} iconSize={10} testId="lh-teaser-opp" />
        </span>
        <span className={`text-[10px] ${muted}`}>{m.opp_projected ? `${m.opp_projected.toFixed(1)} proj` : ""}</span>
      </div>
    </div>
  );
}
